/**
 * Collecting a folder tree from the browser.
 *
 * There are two entirely separate mechanisms and neither covers the other:
 *
 *   * `<input webkitdirectory>` gives a flat FileList where each File carries
 *     `webkitRelativePath` ("Launches/2026-04/clip.mp4"). Simple, but only
 *     reachable by clicking and choosing a folder.
 *   * Drag-and-drop gives DataTransferItems, and the relative path is only
 *     recoverable by walking the filesystem entries yourself. Nothing on the
 *     dropped File objects records where they came from.
 *
 * Both are non-standard and prefixed, but they are what every browser
 * implements, and the alternative is losing the folder structure people
 * actually organise their footage with.
 */

export interface UploadCandidate {
  file: File;
  /** Directory segments relative to the drop, e.g. ['Launches', '2026-04']. */
  segments: string[];
}

/** Path segments a folder should never produce, from macOS/Windows metadata. */
const JUNK = new Set(['.ds_store', 'thumbs.db', 'desktop.ini', '__macosx']);

/**
 * A directory reaching this code as a File.
 *
 * Happens when entry walking is unavailable and we fall back to
 * dataTransfer.files: the folder itself arrives as a zero-byte entry with no
 * type. Uploading it would fail server-side with a confusing message about
 * size, so drop it here instead.
 */
function looksLikeDirectory(file: File): boolean {
  return file.size === 0 && file.type === '';
}

function isJunk(name: string): boolean {
  return JUNK.has(name.toLowerCase()) || name.startsWith('._');
}

function splitPath(path: string): { segments: string[]; name: string } | null {
  const parts = path.split('/').filter(Boolean);
  const name = parts.pop();
  if (!name || isJunk(name) || parts.some(isJunk)) return null;
  return { segments: parts, name };
}

/** Files chosen through an `<input webkitdirectory>` picker. */
export function fromDirectoryInput(files: FileList): UploadCandidate[] {
  const out: UploadCandidate[] = [];

  for (const file of Array.from(files)) {
    // Present on directory picks; empty for a plain multi-file selection.
    const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
    if (!relative) {
      if (!isJunk(file.name)) out.push({ file, segments: [] });
      continue;
    }
    const split = splitPath(relative);
    if (split) out.push({ file, segments: split.segments });
  }

  return out;
}

// --- drag and drop ----------------------------------------------------------

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file(cb: (file: File) => void, err?: (e: unknown) => void): void;
  createReader(): {
    readEntries(cb: (entries: FileSystemEntryLike[]) => void, err?: (e: unknown) => void): void;
  };
}

function readAllEntries(
  reader: ReturnType<FileSystemEntryLike['createReader']>,
): Promise<FileSystemEntryLike[]> {
  // readEntries returns at most 100 per call and signals the end with an empty
  // array. Reading once silently truncates any folder with more than 100
  // items, which for a card of launch photos is most of them.
  return new Promise((resolve, reject) => {
    const all: FileSystemEntryLike[] = [];

    const readBatch = (): void => {
      reader.readEntries((entries) => {
        if (entries.length === 0) {
          resolve(all);
          return;
        }
        all.push(...entries);
        readBatch();
      }, reject);
    };

    readBatch();
  });
}

function entryToFile(entry: FileSystemEntryLike): Promise<File | null> {
  return new Promise((resolve) => {
    entry.file(
      (file) => resolve(file),
      // A file that vanished between the drop and the read is not worth
      // failing the whole upload over.
      () => resolve(null),
    );
  });
}

async function walkEntry(
  entry: FileSystemEntryLike,
  segments: string[],
  out: UploadCandidate[],
  depth: number,
): Promise<void> {
  // A symlink loop would otherwise recurse forever. Sixteen levels is far
  // deeper than any real media tree.
  if (depth > 16 || isJunk(entry.name)) return;

  if (entry.isFile) {
    const file = await entryToFile(entry);
    if (file) out.push({ file, segments });
    return;
  }

  if (entry.isDirectory) {
    const entries = await readAllEntries(entry.createReader());
    for (const child of entries) {
      await walkEntry(child, [...segments, entry.name], out, depth + 1);
    }
  }
}

/**
 * Files from a drop, with folder structure preserved.
 *
 * Falls back to `dataTransfer.files` when entry access is unavailable, which
 * loses the tree but still uploads the files rather than doing nothing.
 */
export async function fromDrop(dataTransfer: DataTransfer): Promise<UploadCandidate[]> {
  const items = Array.from(dataTransfer.items ?? []);

  const entries = items
    .map((item) => {
      const get = (item as DataTransferItem & {
        webkitGetAsEntry?: () => FileSystemEntryLike | null;
      }).webkitGetAsEntry;
      return typeof get === 'function' ? get.call(item) : null;
    })
    .filter((entry): entry is FileSystemEntryLike => entry !== null);

  if (entries.length === 0) {
    return Array.from(dataTransfer.files)
      .filter((file) => !isJunk(file.name) && !looksLikeDirectory(file))
      .map((file) => ({ file, segments: [] }));
  }

  const out: UploadCandidate[] = [];
  for (const entry of entries) {
    // Top-level entries contribute their own name as a segment only when they
    // are directories; a dropped loose file belongs at the destination root.
    if (entry.isFile) {
      const file = await entryToFile(entry);
      if (file && !isJunk(file.name)) out.push({ file, segments: [] });
    } else {
      await walkEntry(entry, [], out, 0);
    }
  }
  return out;
}

/** Groups candidates by directory so each tree level is resolved once. */
export function groupByDirectory(
  candidates: UploadCandidate[],
): Map<string, UploadCandidate[]> {
  const groups = new Map<string, UploadCandidate[]>();
  for (const candidate of candidates) {
    const key = candidate.segments.join('/');
    const list = groups.get(key) ?? [];
    list.push(candidate);
    groups.set(key, list);
  }
  return groups;
}
